import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAdminRedeemRequests, 
  acceptAdminRedemptionRequest, 
  rejectAdminRedemptionRequest, 
  markAdminAsDelivered,
  getAdminRewards
} from '../../api/admin';
import AdminSecureStorage from '../../utils/adminSecureStorage';

interface RedemptionRequest {
  id: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'DELIVERED';
  requestedAt: string;
  processedAt?: string;
  rejectionReason?: string;
  customer: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  reward: {
    id: string;
    title: string;
    description: string;
    requiredPoints: number;
    imageUrl?: string;
    owner?: {
      id: string;
      businessName: string;
      originalEmail: string;
    };
  };
  pointsUsed: number;
  deliveryInfo?: {
    address: string;
    phone: string;
    notes?: string;
  };
}

interface Reward {
  id: string;
  title: string;
  description: string;
  requiredPoints: number;
  category: string;
  isActive: boolean;
  imageUrl?: string;
  owner?: {
    id: string;
    businessName: string;
    originalEmail: string;
  };
}


const AdminRedemptionRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<RedemptionRequest[]>([]);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    rewardId: ''
  });
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [bulkDeliverLoading, setBulkDeliverLoading] = useState(false);
  const [deliveringIds, setDeliveringIds] = useState<string[]>([]);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [acceptingIds, setAcceptingIds] = useState<string[]>([]);

  useEffect(() => {
    if (!AdminSecureStorage.isAdminTokenValid()) {
      navigate('/admin/login');
      return;
    }
    loadRequests();
  }, [navigate, currentPage, filters]);

  // Reset to first page whenever customer or other filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCustomerId, filters.status, filters.rewardId]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const [requestsResponse, rewardsResponse] = await Promise.all([
        getAdminRedeemRequests(
          currentPage, 
          20, 
          filters.status || undefined, 
          filters.rewardId || undefined
        ),
        getAdminRewards(1, 1000) // Get all rewards for filter
      ]);

      console.log('Requests response:', requestsResponse);
      console.log('Rewards response:', rewardsResponse);

      // Check different possible response structures
      const requestsData = requestsResponse.data?.requests || requestsResponse.data?.data?.requests || requestsResponse.data || requestsResponse;
      const totalPagesData = requestsResponse.data?.totalPages || requestsResponse.data?.data?.totalPages || 1;
      const rewardsData = rewardsResponse.data?.rewards || rewardsResponse.data?.data?.rewards || rewardsResponse.data || [];

      console.log('Parsed requests data:', requestsData);
      console.log('Parsed total pages:', totalPagesData);
      console.log('Parsed rewards data:', rewardsData);

      // More robust data extraction
      let finalRequestsData = [];
      if (Array.isArray(requestsData)) {
        finalRequestsData = requestsData;
      } else if (requestsData && typeof requestsData === 'object' && requestsData.rows) {
        finalRequestsData = requestsData.rows; // Sequelize pagination format
      } else if (requestsData && typeof requestsData === 'object' && requestsData.data) {
        finalRequestsData = Array.isArray(requestsData.data) ? requestsData.data : [];
      }

      let finalRewardsData = [];
      if (Array.isArray(rewardsData)) {
        finalRewardsData = rewardsData;
      } else if (rewardsData && typeof rewardsData === 'object' && rewardsData.rows) {
        finalRewardsData = rewardsData.rows;
      }

      console.log('Final requests data:', finalRequestsData);
      console.log('Final rewards data:', finalRewardsData);

      setRequests(finalRequestsData);
      setTotalPages(totalPagesData);
      setRewards(finalRewardsData);
    } catch (error: any) {
      console.error('Error loading requests:', error);
      setError(error.response?.data?.error || 'Failed to load requests');
      
      if (error.response?.status === 401) {
        AdminSecureStorage.clearAll();
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Build unique customers list from loaded requests
  const uniqueCustomers = (requests || [])
    .map(r => r.customer)
    .filter(Boolean)
    .reduce((acc: any[], c: any) => {
      if (!acc.find(x => x.id === c.id)) acc.push(c);
      return acc;
    }, [])
    .map(c => ({
      id: c.id,
      label: c.email || c.username || c.id
    }));

  const filteredCustomers = uniqueCustomers.filter(c =>
    c.label.toLowerCase().includes(customerSearch.toLowerCase())
  );

  // Apply client-side filtering by selected customer
  const filteredRequests = selectedCustomerId
    ? (requests || []).filter(r => r.customer?.id === selectedCustomerId)
    : (requests || []);

  const handleAcceptRequest = async (requestId: string) => {
    try {
      setAcceptingIds(prev => Array.from(new Set([...prev, requestId])));
      await acceptAdminRedemptionRequest(requestId);
      loadRequests();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to accept request');
    } finally {
      setAcceptingIds(prev => prev.filter(id => id !== requestId));
    }
  };

  const handleRejectRequest = async () => {
    if (!rejectingRequestId) return;
    
    try {
      setRejectSubmitting(true);
      await rejectAdminRedemptionRequest(rejectingRequestId, rejectionReason);
      setShowRejectModal(false);
      setRejectingRequestId(null);
      setRejectionReason('');
      loadRequests();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to reject request');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleMarkAsDelivered = async () => {
    if (selectedRequests.length === 0) return;
    
    try {
      setBulkDeliverLoading(true);
      await markAdminAsDelivered(selectedRequests);
      setSelectedRequests([]);
      loadRequests();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to mark as delivered');
    } finally {
      setBulkDeliverLoading(false);
    }
  };

  const handleDeliverSingle = async (requestId: string) => {
    try {
      setDeliveringIds(prev => Array.from(new Set([...prev, requestId])));
      await markAdminAsDelivered([requestId]);
      setSelectedRequests(prev => prev.filter(id => id !== requestId));
      loadRequests();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to mark as delivered');
    } finally {
      setDeliveringIds(prev => prev.filter(id => id !== requestId));
    }
  };

  const toggleRequestSelection = (requestId: string) => {
    setSelectedRequests(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  // Small status dot classes
  const getStatusDotClass = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-500';
      case 'ACCEPTED':
        return 'bg-blue-600';
      case 'REJECTED':
        return 'bg-red-600';
      case 'DELIVERED':
        return 'bg-emerald-600';
      default:
        return 'bg-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="text-gray-600">Loading requests...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modern Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-700 shadow-xl">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Redemption Requests</h1>
              <p className="text-slate-300">Manage customer redemption requests efficiently</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-slate-300 hover:text-white transition-colors px-4 py-2 rounded-lg hover:bg-slate-800"
              >
                Dashboard
              </button>
              <button
                onClick={loadRequests}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}


        {/* Modern Filters Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-semibold text-gray-900">Filters</h3>
            </div>
            <button
              onClick={() => {
                setFilters({ status: '', rewardId: '' });
                setSelectedCustomerId('');
                setCustomerSearch('');
                setCustomerDropdownOpen(false);
              }}
              className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="REJECTED">Rejected</option>
                <option value="DELIVERED">Delivered</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Reward</label>
              <select
                value={filters.rewardId}
                onChange={(e) => setFilters({...filters, rewardId: e.target.value})}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">All Rewards</option>
                {rewards.map(reward => (
                  <option key={reward.id} value={reward.id}>
                    {reward.title} ({reward.requiredPoints} pts)
                  </option>
                ))}
              </select>
            </div>
            <div className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-3 text-left">Customer</label>
              <button
                type="button"
                onClick={() => setCustomerDropdownOpen(v => !v)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-left focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                {selectedCustomerId
                  ? (uniqueCustomers.find(c => c.id === selectedCustomerId)?.label || 'Select customer')
                  : 'All Customers'}
              </button>
              {customerDropdownOpen && (
                <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg p-2">
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search customer..."
                    className="w-full mb-2 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="max-h-56 overflow-auto">
                    <button
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg text-sm"
                      onClick={() => { setSelectedCustomerId(''); setCustomerDropdownOpen(false); }}
                    >
                      All Customers
                    </button>
                    {filteredCustomers.map(c => (
                      <button
                        key={c.id}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg text-sm ${selectedCustomerId === c.id ? 'bg-blue-50' : ''}`}
                        onClick={() => { setSelectedCustomerId(c.id); setCustomerDropdownOpen(false); }}
                      >
                        {c.label}
                      </button>
                    ))}
                    {filteredCustomers.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">No customers</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modern Requests List */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Redemption Requests</h3>
              </div>
              <div className="flex items-center gap-3 justify-start">
                <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">
                  {filteredRequests?.length || 0} Total
                </div>
                {selectedRequests.length > 0 && (
                  <button
                    onClick={handleMarkAsDelivered}
                    disabled={bulkDeliverLoading}
                    className={`px-4 py-2 rounded-lg transition-colors text-white ${
                      bulkDeliverLoading ? 'bg-emerald-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'
                    }`}
                  >
                    {bulkDeliverLoading ? 'Delivering...' : `Mark Delivered (${selectedRequests.length})`}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    <input
                      type="checkbox"
                      checked={selectedRequests.length === (requests?.length || 0) && (requests?.length || 0) > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRequests(requests?.map(r => r.id) || []);
                        } else {
                          setSelectedRequests([]);
                        }
                      }}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reward
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Points
                  </th>
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                     Status
                   </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                    Requested
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredRequests && filteredRequests.length > 0 ? filteredRequests.map((request) => (
                  <tr key={request.id} className={selectedRequests.includes(request.id) ? 'bg-blue-50' : ''}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedRequests.includes(request.id)}
                        onChange={() => toggleRequestSelection(request.id)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {request.customer?.firstName && request.customer?.lastName 
                            ? `${request.customer?.firstName} ${request.customer?.lastName}`
                            : request.customer?.email || 'Unknown Customer'
                          }
                        </div>
                        <div className="text-sm text-gray-500">{request.customer?.email || 'No email'}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-left">
                      <div className="flex items-center">
                        {request.reward?.imageUrl && (
                          <img className="h-9 w-9 rounded-md object-cover mr-3" src={request.reward?.imageUrl} alt={request.reward?.title || 'Reward'} />
                        )}
                        <div>
                          <div className="font-medium text-gray-900 truncate max-w-xs">{request.reward?.title || 'Unknown Reward'}</div>
                          <div className="text-gray-500 truncate max-w-xs">{request.reward?.description || 'No description available'}</div>
                          {request.reward?.owner && (
                            <div className="text-xs text-gray-400">{request.reward?.owner?.businessName || request.reward?.owner?.originalEmail}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {request.pointsUsed.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-block h-2.5 w-2.5 rounded-full ${getStatusDotClass(request.status)}`}
                        title={request.status === 'REJECTED' && request.rejectionReason ? `Rejected: ${request.rejectionReason}` : request.status}
                        aria-label={request.status}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                      {formatDate(request.requestedAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium">
                      {request.status === 'PENDING' && (
                        <>
                          <button
                            onClick={() => handleAcceptRequest(request.id)}
                            disabled={acceptingIds.includes(request.id)}
                            className={`mr-3 ${acceptingIds.includes(request.id) ? 'text-green-400 cursor-not-allowed' : 'text-green-600 hover:text-green-900'}`}
                          >
                            {acceptingIds.includes(request.id) ? 'Accepting...' : 'Accept'}
                          </button>
                          <button
                            onClick={() => {
                              setRejectingRequestId(request.id);
                              setShowRejectModal(true);
                            }}
                            className="text-red-600 hover:text-red-900 mr-3"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {request.status === 'ACCEPTED' && (
                        <button
                          onClick={() => handleDeliverSingle(request.id)}
                          disabled={deliveringIds.includes(request.id)}
                          className={` ${
                            deliveringIds.includes(request.id)
                              ? 'text-emerald-400 cursor-not-allowed'
                              : 'text-emerald-600 hover:text-emerald-900'
                          }`}
                        >
                          {deliveringIds.includes(request.id) ? 'Delivering...' : 'Deliver'}
                        </button>
                      )}
                      {request.status === 'REJECTED' && request.rejectionReason && (
                        <div className="text-xs text-red-500" title={request.rejectionReason}>Rejected</div>
                      )}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-left text-gray-500">
                      <div className="flex flex-col">
                        <p className="text-lg font-medium text-gray-900 mb-2 text-left">No redemption requests found</p>
                        <p className="text-sm text-gray-500 text-left">Try adjusting your filters or check back later.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Redemption Request</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  disabled={rejectSubmitting}
                  className={`w-full border border-gray-300 rounded-md px-3 py-2 ${rejectSubmitting ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  rows={3}
                  placeholder="Enter reason for rejection..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectingRequestId(null);
                    setRejectionReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectRequest}
                  disabled={rejectSubmitting}
                  className={`px-4 py-2 text-white rounded-md ${rejectSubmitting ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {rejectSubmitting ? 'Rejecting...' : 'Reject Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRedemptionRequests;
